import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useAuth } from "app/authHooks";
import loadingStore from "core/utils/loading";
import { observer } from "mobx-react";
import { useNavigate, useParams } from "react-router";
import userRoleStore from "../api";
import { ReactNode, useEffect, useState } from "react";
import { hasPermission, Permission } from "core/utils/roles";
import * as yup from "yup";
import { enqueueSnackbar } from "notistack";
import PageWrapper from "components/PageWrapper";
import { Formik } from "formik";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { BackendRoles } from "../model";

function AddEditUserRoles() {
  const initialValues = {
    name: "",
    description: "",
    customer_id: "",
    role_rank: "",
    backend_roles: [],
  } as any;

  const { attributes } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const [backendRoleOptions, setBackendRoleOptions] = useState<BackendRoles[]>(
    []
  );

  const userRolesScheme = yup.object({
    name: yup
      .string()
      .min(2, "Name must be at least 2 characters.")
      .max(10, "Name must be at most 10 characters.")
      .matches(/^[a-zA-Z]+$/, "Name must contain only letters (a-z, A-Z).")
      .when([], {
        is: () => !isEdit,
        then: (schema) => schema.required("Name is required."),
      }),
    description: yup
      .string()
      .min(2, "Description must be at least 2 characters.")
      .max(255, "Description must be at most 255 characters.")
      .required("Description is required."),
    customer_id: yup
      .string()
      .min(7, "Customer Id must be at least 7 characters.")
      .max(10, "Customer Id must be at most 10 characters.")
      .required("Customer Id is required."),
    role_rank: yup
      .number()
      .integer("Rank must be an integer.")
      .min(0, "Rank must be at least 0.")
      .max(1000, "Rank must be at most 1000.")
      .required("Rank is required."),
    backend_roles: yup
      .array()
      .of(
        yup.number().positive("Each backend role Id must be a positive number.")
      ),
  });

  const {
    getUserRoleById,
    createUserRole,
    updateUserRole,
    getUserBackendRole,
  } = userRoleStore;
  const { loading } = loadingStore;

  const isEdit = !!params?.id;
  const [dataValue, setDataValue] = useState<any>(initialValues);
  const canUpdateUserRole = hasPermission(
    "update:roles" as Permission
  );

  useEffect(() => {
    onInit();
  }, []);

  const onInit = async () => {
    setBackendRoleOptions(await getUserBackendRole());
    if (isEdit) {
      const userRoles = await getUserRoleById(params?.id);
      setDataValue(userRoles);
    } else {
      setDataValue(initialValues);
    }
  };

  const handleSubmitForm = async (values: any) => {
    if (isEdit && params?.id) {
      const response = await updateUserRole(params.id, {
        description: values.description,
        customer_id: values.customer_id,
        role_rank: values.role_rank,
        backend_roles: values.backend_roles || [],
      });
      if (response) {
        enqueueSnackbar("User role has been successfully updated!!!", {
          variant: "success",
        });
        navigate("/security/roles");
      }
    } else {
      const response = await createUserRole({
        name: values.name,
        description: values.description,
        customer_id: values.customer_id,
        role_rank: values.role_rank,
        backend_roles: values.backend_roles || [],
      });
      if (response) {
        enqueueSnackbar("User role has been successfully created!!!", {
          variant: "success",
        });
        navigate("/security/roles");
      }
    }
  };

  const formatError = (error: any): string | undefined => {
    if (typeof error === "string") {
      return error;
    } else if (Array.isArray(error)) {
      return error.join(", ");
    } else if (typeof error === "object") {
      console.log(error);
      if (error?.type) {
        return error.type;
      }
      return "An error occurred";
    }
    return undefined;
  };

  interface CardProps {
    title: ReactNode;
    cardContent: ReactNode;
  }

  const CardComponent: React.FC<CardProps> = ({ title, cardContent }) => {
    return (
      <div
        style={{
          border: "1px solid #ddd",
          borderRadius: 8,
          padding: "16px",
          width: "100%",
        }}
      >
        <header style={{ borderBottom: "1px solid #ddd", width: "100%" }}>
          {title}
        </header>
        <main style={{ marginTop: "20px" }}>{cardContent}</main>
      </div>
    );
  };

  return (
    <PageWrapper>
      <Box sx={{ py: 4, px: 4 }}>
        <Box
          sx={{
            cursor: "pointer",
            fontSize: "14px",
            lineHeight: "20px",
            marginBottom: "16px",
          }}
        >
          <Link underline="hover" onClick={() => navigate("/security/roles")}>
            <ArrowBackIcon
              sx={{ width: "16px", height: "16px", marginRight: "4px" }}
            />{" "}
            Back to User Role
          </Link>
        </Box>
        <Formik
          onSubmit={handleSubmitForm}
          initialValues={dataValue}
          validationSchema={userRolesScheme}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            handleChange,
            handleSubmit,
            setFieldValue,
            touched,
            handleBlur,
          }) => {
            return (
              <form onSubmit={handleSubmit}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <CardComponent
                    title={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          variant="h3"
                          margin={"10px"}
                          sx={{ fontWeight: 700 }}
                        >
                          {isEdit
                            ? canUpdateUserRole
                              ? "Edit User Role"
                              : "View User Role"
                            : "Add User Role"}
                        </Typography>
                      </Box>
                    }
                    cardContent={
                      <Box sx={{ "& > *:not(:last-child)": { mb: 2 } }}>
                        <Grid container spacing={2}>
                          <Grid
                            item
                            md={6}
                            xs={10}
                            style={{ textAlign: "left" }}
                          >
                            <FormControl style={{ width: "100%" }}>
                              <TextField
                                fullWidth
                                type="text"
                                label="Name"
                                name="name"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.name}
                                error={!!touched.name && !!errors?.name}
                                helperText={
                                  touched?.name
                                    ? formatError(errors?.name)
                                    : undefined
                                }
                                sx={{
                                  gridColumn: "span 4",
                                }}
                                InputLabelProps={{ shrink: true }}
                                disabled={!canUpdateUserRole || isEdit}
                              />
                            </FormControl>
                          </Grid>
                          <Grid
                            item
                            md={6}
                            xs={10}
                            style={{ textAlign: "left" }}
                          >
                            <FormControl style={{ width: "100%" }}>
                              <TextField
                                fullWidth
                                type="text"
                                label="Description"
                                name="description"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.description}
                                error={
                                  !!touched.description && !!errors?.description
                                }
                                helperText={
                                  touched?.description
                                    ? formatError(errors?.description)
                                    : undefined
                                }
                                sx={{
                                  gridColumn: "span 4",
                                }}
                                InputLabelProps={{ shrink: true }}
                                disabled={!canUpdateUserRole}
                              />
                            </FormControl>
                          </Grid>
                          <Grid
                            item
                            md={6}
                            xs={10}
                            style={{ textAlign: "left" }}
                          >
                            <FormControl style={{ width: "100%" }}>
                              <TextField
                                fullWidth
                                type="text"
                                label="Customer Id"
                                name="customer_id"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.customer_id}
                                error={
                                  !!touched.customer_id && !!errors?.customer_id
                                }
                                helperText={
                                  touched?.customer_id
                                    ? formatError(errors?.customer_id)
                                    : undefined
                                }
                                sx={{
                                  gridColumn: "span 4",
                                }}
                                InputLabelProps={{ shrink: true }}
                                disabled={!canUpdateUserRole}
                              />
                            </FormControl>
                          </Grid>
                          <Grid
                            item
                            md={6}
                            xs={10}
                            style={{ textAlign: "left" }}
                          >
                            <FormControl
                              style={{ width: "100%" }}
                              error={
                                !!touched.backend_roles &&
                                !!errors.backend_roles
                              }
                            >
                              <InputLabel id="backend_roles_label">
                                Backend Role
                              </InputLabel>
                              <Select
                                labelId="backend_roles_label"
                                label="Backend Role"
                                name="backend_roles"
                                multiple
                                fullWidth
                                value={values.backend_roles}
                                onChange={(e) => {
                                  const {
                                    target: { value },
                                  } = e;
                                  setFieldValue(
                                    "backend_roles",
                                    typeof value === "string"
                                      ? value.split(",").map(Number)
                                      : value.map(Number)
                                  );
                                }}
                                renderValue={(selected) =>
                                  backendRoleOptions
                                    .filter((option) =>
                                      selected.includes(option.id)
                                    )
                                    .map((option) => option.description)
                                    .join(", ")
                                }
                                onBlur={handleBlur}
                                disabled={!canUpdateUserRole}
                              >
                                {backendRoleOptions.map((role) => (
                                  <MenuItem key={role.id} value={role.id}>
                                    <Checkbox
                                      checked={values.backend_roles?.includes(
                                        role.id
                                      )}
                                    />
                                    <ListItemText primary={role.description} />
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid
                            item
                            md={6}
                            xs={10}
                            style={{ textAlign: "left" }}
                          >
                            <FormControl style={{ width: "100%" }}>
                              <TextField
                                fullWidth
                                type="number"
                                label="Rank"
                                name="role_rank"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.role_rank}
                                error={
                                  !!touched.role_rank && !!errors?.role_rank
                                }
                                helperText={
                                  touched?.role_rank
                                    ? formatError(errors?.role_rank)
                                    : undefined
                                }
                                sx={{
                                  gridColumn: "span 4",
                                }}
                                InputLabelProps={{ shrink: true }}
                                disabled={!canUpdateUserRole}
                              />
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Box>
                    }
                  />
                </Box>

                <Button
                  style={{ marginTop: 10 }}
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={loading || !canUpdateUserRole}
                  endIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {isEdit
                    ? loading
                      ? "UPDATING"
                      : "UPDATE"
                    : loading
                      ? "SAVING"
                      : "SAVE"}
                </Button>
              </form>
            );
          }}
        </Formik>
      </Box>
    </PageWrapper>
  );
}

export default observer(AddEditUserRoles);
